import _ from 'lodash'

export default {
  name: 'project-template-playback-controls',
  props: {
    previewSvgGetter: {
      required: true,
      type: Function
    }
  },
  data () {
    return {
      percentage: 0,
      progressColor: '#a5a3a4',
      backColor: '#dcdfe5',
      timeElapsed: '00:00',
      totalDuration: '&infin;',
      isPreviewLoaded: false,
      // interval to pull playback information
      playbackIntervalId: null,
      playbackWatchTimeout: 1000,
      // interval to progress
      progressSimulationIntervalId: null,
      progressSimulationTimeout: 150,
      progressSimulationPosition: 0,
      durationRaw: 0,
      play: true
    }
  },
  async created () {
    this.resetPlaybackControls()
    this.setupPlaybackProgressWatcher()
    document.addEventListener('visibilitychange', this.visibilityHandler)
  },
  async beforeUnmount () {
    document.removeEventListener('visibilitychange', this.visibilityHandler)
    this.resetPlaybackControls()
  },
  methods: {
    visibilityHandler () {
      if (document.visibilityState === 'visible') {
        this.playMedia()
      } else {
        this.stopMedia()
      }
    },
    /**
     * Simulate video position based off the timing of the media.
     * Feed-forward approach to make sure progress bar is smooth
     */
    startPlaybackSimulation () {
      // convert duration into MS, so both timeout and duration are in the same units
      const duration = Math.floor(this.durationRaw * 1000)
      this.progressSimulationIntervalId = setInterval(() => {
        this.progressSimulationPosition += this.progressSimulationTimeout
        // calculate percentage based on how many `simulation intervals` within `duration`
        this.percentage = (this.progressSimulationPosition / duration) * 100
        if (this.progressSimulationPosition >= duration) {
          this.stopPlaybackSimulation()
        }
      }, this.progressSimulationTimeout)
    },
    stopPlaybackSimulation () {
      if (this.progressSimulationIntervalId !== null) {
        clearInterval(this.progressSimulationIntervalId)
      }
      this.percentage = 0
      this.progressSimulationPosition = 0
      this.progressSimulationIntervalId = null
    },
    /**
     * Format into nice view
     * @param {Format} secondsRaw - seconds as is from the SVG player
     */
    formatPlaybackTime (secondsRaw) {
      if (!_.isNumber(secondsRaw)) {
        return '&infin;'
      }
      const hours = Math.floor(secondsRaw / 3600)
      const minutes = Math.floor((secondsRaw % 3600) / 60)
      const seconds = Math.floor(secondsRaw % 60)
      const format = (n) => n < 10 ? '0' + n : n

      // add hour if there's any
      if (hours > 0) {
        return `${format(hours)}:${format(minutes)}:${format(seconds)}`
      }
      // minutes:seconds by default
      return `${format(minutes)}:${format(seconds)}`
    },

    /**
     * Handle SVG messages. This function called from within parent component
     * via $refs
     * @param {*} event
     */
    handleSVGMessage (event) {
      if (_.isUndefined(event.data.command)) {
        return
      }
      // processing commands
      switch (event.data.command) {
        case 'info':
          this.timeElapsed = this.formatPlaybackTime(event.data.response.position)
          this.totalDuration = this.formatPlaybackTime(event.data.response.duration)

          // NOTE: An approach with "real" playback progress is shaky and provides
          // bad experience to the end user. Instead, simulation of the progress provides nice
          // experience and isn't too far off the "real" one.
          // this.percentage = Math.floor(event.data.response.position / event.data.response.duration * 100)

          if (_.isNumber(event.data.response.duration)) {
            this.durationRaw = Math.floor(event.data.response.duration)
            if (!this.progressSimulationIntervalId || Math.floor(event.data.response.position) === 0) {
              this.stopPlaybackSimulation()
              this.startPlaybackSimulation()
            }
          } else {
            // 95 is a value for infinite length media
            this.percentage = 95
          }
          break
        case 'stop':
          if (event.data.response.stop) {
            this.resetPlaybackControls()
            this.play = false
          }
          break
        case 'play':
          if (event.data.response.start) {
            this.play = true
            this.setupPlaybackProgressWatcher()
          }
          break
      }
    },
    /**
     * Send a messages to the SVG from this.previewSvg
     * @param {*} msg
     */
    postMessageToSVG (msg) {
      const svg = this.previewSvgGetter()
      if (svg) {
        svg.postMessage(msg, '*')
      }
    },

    stopMedia () {
      this.postMessageToSVG({ command: 'stop', meta: {} })
    },

    playMedia () {
      this.postMessageToSVG({ command: 'play', meta: {} })
    },

    /**
     * Switcher to send play/stop command based off this.play property
     */
    switchPlaybackState (e) {
      if (this.play) {
        this.stopMedia()
      } else {
        this.playMedia()
      }
    },
    /**
     * This is pull watcher to send INFO command with an interval
     * this.playbackWatchInterval, so that keep play progress in sync
     */
    setupPlaybackProgressWatcher () {
      // watcher already there
      if (this.playbackIntervalId) {
        return
      }
      this.postMessageToSVG({ command: 'info', meta: {} })
      this.playbackIntervalId = setInterval(() => {
        this.postMessageToSVG({ command: 'info', meta: {} })
      }, this.playbackWatchTimeout)
    },
    /**
     * Clear interval and stop INFO pull
     */
    resetPlaybackProgressWatcher () {
      clearInterval(this.playbackIntervalId)
      this.playbackIntervalId = null
    },
    /**
     * Set player to the initial state (autoplay=on)
     */
    resetPlaybackControls () {
      this.stopPlaybackSimulation()
      this.resetPlaybackProgressWatcher()

      // reset to initial values
      this.play = true
      this.timeElapsed = this.formatPlaybackTime(0)
    }
  }
}
